import { createZipStream } from "@frontend-shared/utils";
import type { PropsWithChildren, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { DownloadIndicator } from "@/components/DownloadIndicator";
import type {
  IMediaDownloadState,
  IStartMediaDownloadProps,
} from "@/contexts/MediaDownloadContext";
import { MediaDownloadContext } from "@/contexts/MediaDownloadContext";

interface ISetMeetingDownloadStateProps {
  meetingUuid: string;
  state: IMediaDownloadState | null;
}

// NOTE: Owns every media download so its progress lives above the routes and is
// not tied to the RecordingPlayBar's lifecycle. When the user navigates away and
// back, the play bar re-reads the in-flight progress from here, the global
// DownloadIndicator keeps showing progress + a cancel control on every page, and
// a refresh/tab-close is guarded by a beforeunload prompt (NSAI-3838).
const MediaDownloadProvider = ({ children }: PropsWithChildren): ReactNode => {
  const { t } = useTranslation("callAssistant");

  const [downloads, setDownloads] = useState<
    Record<string, IMediaDownloadState>
  >({});
  // NOTE: Synchronous mirror of the active meetings so a second click can't kick
  // off a duplicate download before the async state update lands.
  const activeMeetingsRef = useRef<Set<string>>(new Set());
  // NOTE: One AbortController per in-flight download so cancelDownload can stop
  // the fetch/stream from the global indicator (or anywhere).
  const abortControllersRef = useRef<Map<string, AbortController>>(new Map());
  // NOTE: Meetings whose download was aborted by a bulk clearAll (logout /
  // account switch) — their abort should not surface a "cancelled" toast.
  const silentAbortsRef = useRef<Set<string>>(new Set());

  const setMeetingState = useCallback(
    ({ meetingUuid, state }: ISetMeetingDownloadStateProps): void => {
      setDownloads((previous) => {
        if (state === null) {
          // NOTE: Cleanup always targets a meeting that was added at start, so
          // the not-present path is a defensive no-op rather than a real path.
          /* istanbul ignore next */
          if (!(meetingUuid in previous)) {
            return previous;
          }
          const next = { ...previous };
          delete next[meetingUuid];
          return next;
        }
        return { ...previous, [meetingUuid]: state };
      });
    },
    [],
  );

  const startDownload = useCallback(
    ({
      meetingUuid,
      url,
      filename,
      mediaType,
      errorKey,
      label,
      archive,
    }: IStartMediaDownloadProps): void => {
      // NOTE: Defensive double-start guard — the play bar disables/replaces the
      // trigger while a download for this meeting runs, so this is only a
      // race-condition backstop and is not exercised in E2E.
      /* istanbul ignore next */
      if (activeMeetingsRef.current.has(meetingUuid)) {
        return;
      }
      activeMeetingsRef.current.add(meetingUuid);

      const controller = new AbortController();
      abortControllersRef.current.set(meetingUuid, controller);

      const run = async (): Promise<void> => {
        let reader:
          | ReadableStreamDefaultReader<Uint8Array<ArrayBuffer>>
          | undefined;
        try {
          setMeetingState({
            meetingUuid,
            state: { downloadingType: mediaType, downloadProgress: 0, label },
          });

          const response = await fetch(url, { signal: controller.signal });
          if (response.status === 202) {
            toast.error(t("details.play_bar.processing"));
            return;
          }

          if (!response.ok) {
            throw new Error("download_failed");
          }

          // NOTE: The response is confirmed streaming, so announce the real start
          // here — not at completion, which is why the old toast read "Download
          // started" only once the file had already finished (NSAI-3838).
          toast.success(t("details.play_bar.toasts.download_started"));

          const contentLength = Number(
            response.headers.get("content-length") || "0",
          );

          reader = response.body?.getReader();
          // NOTE: A 2xx response always exposes a readable body stream, so this
          // is a type-narrowing guard rather than a reachable runtime path.
          /* istanbul ignore next */
          if (!reader) {
            throw new Error("download_failed");
          }

          // NOTE: Collect the chunk *views* (not value.buffer): a stream chunk
          // can be a view into a larger/shared ArrayBuffer, so pushing .buffer
          // could splice in bytes outside the view and corrupt the file. Blob
          // honors each view's byteOffset/byteLength.
          const chunks: BlobPart[] = [];
          let receivedBytes = 0;
          let lastProgress = 0;

          // NOTE: NSAI-3847 — "Download all" writes a real .zip: the summary
          // and the transcript go in as complete entries, then the recording is
          // streamed into a third entry as it arrives. The zip writer hands
          // back the very chunk views it was given for stored data, so the
          // archive costs no second copy of the recording — the same bytes go
          // into `chunks` either way.
          const zipStream = archive
            ? createZipStream({
                onChunk: (chunk) => chunks.push(chunk),
                modifiedAt: archive.modifiedAt,
              })
            : null;

          if (zipStream && archive) {
            archive.textFiles.forEach((textFile) => {
              zipStream.addTextFile(textFile);
            });
            zipStream.startStreamedFile({ name: archive.mediaEntryName });
          }

          for (;;) {
            const { done: isDone, value } = await reader.read();
            if (isDone) {
              break;
            }
            if (zipStream) {
              zipStream.writeStreamedChunk(value);
            } else {
              chunks.push(value);
            }
            receivedBytes += value.byteLength;
            if (contentLength > 0) {
              // NOTE: Clamp to 100 (a mismatched content-length must never yield
              // >100%) and only push state when the integer percent actually
              // changes, so a small-chunk download can't re-render consumers on
              // every read.
              const nextProgress = Math.min(
                100,
                Math.round((receivedBytes / contentLength) * 100),
              );
              if (nextProgress !== lastProgress) {
                lastProgress = nextProgress;
                setMeetingState({
                  meetingUuid,
                  state: {
                    downloadingType: mediaType,
                    downloadProgress: nextProgress,
                    label,
                  },
                });
              }
            }
          }

          if (zipStream) {
            zipStream.endStreamedFile();
            zipStream.finish();
          }

          const blob = new Blob(chunks);
          const objectUrl = URL.createObjectURL(blob);

          const link = document.createElement("a");
          link.href = objectUrl;
          link.download = filename;
          link.rel = "noopener";
          document.body.appendChild(link);
          link.click();
          link.remove();
          URL.revokeObjectURL(objectUrl);

          toast.success(t("details.play_bar.toasts.download_complete"));
        } catch {
          if (controller.signal.aborted) {
            // NOTE: A user-initiated cancelDownload() aborts the fetch/reader and
            // lands here — a benign cancellation. A bulk clearAll (logout) also
            // aborts, but should stay silent (tracked in silentAbortsRef).
            if (silentAbortsRef.current.has(meetingUuid)) {
              silentAbortsRef.current.delete(meetingUuid);
            } else {
              toast.info(t("details.play_bar.toasts.download_cancelled"));
            }
          } else {
            toast.error(t(errorKey));
          }
        } finally {
          // NOTE: Release the stream lock / close the connection promptly on any
          // exit path instead of relying solely on AbortController propagation.
          if (reader) {
            await reader.cancel().catch(() => undefined);
          }
          activeMeetingsRef.current.delete(meetingUuid);
          abortControllersRef.current.delete(meetingUuid);
          setMeetingState({ meetingUuid, state: null });
        }
      };

      // NOTE: we need void to silence the eslint rule because we are using an async function
      void run(); // NOSONAR
    },
    [setMeetingState, t],
  );

  const cancelDownload = useCallback((meetingUuid: string): void => {
    const controller = abortControllersRef.current.get(meetingUuid);
    // NOTE: The cancel control only renders while a download (and thus its
    // controller) exists, so the missing-controller path is not reachable.
    /* istanbul ignore else */
    if (controller) {
      controller.abort();
    }
  }, []);

  // NOTE: Abort and drop every in-flight download without surfacing per-download
  // "cancelled" toasts — invoked on logout / account switch so one user's
  // download (and its meeting title/progress) can't leak into the next session.
  const clearAll = useCallback((): void => {
    abortControllersRef.current.forEach((controller, meetingUuid) => {
      silentAbortsRef.current.add(meetingUuid);
      controller.abort();
    });
    abortControllersRef.current.clear();
    activeMeetingsRef.current.clear();
    setDownloads({});
  }, []);

  const hasActiveDownloads = Object.keys(downloads).length > 0;

  // NOTE: A blob download is held in memory and dies with the page, so warn the
  // user before a refresh / tab-close would silently drop an in-flight download.
  // In-app navigation is intentionally NOT guarded — the download continues and
  // the indicator follows the user (NSAI-3838).
  useEffect(() => {
    if (!hasActiveDownloads) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // NOTE: preventDefault() triggers the native "Leave site?" prompt in
      // modern browsers; older Chrome/Edge also needed returnValue. Object.assign
      // sets the (deprecated) property without a type assertion.
      event.preventDefault();
      Object.assign(event, { returnValue: "" });
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasActiveDownloads]);

  const value = useMemo(
    () => ({ downloads, startDownload, cancelDownload, clearAll }),
    [downloads, startDownload, cancelDownload, clearAll],
  );

  return (
    <MediaDownloadContext.Provider value={value}>
      {children}
      <DownloadIndicator />
    </MediaDownloadContext.Provider>
  );
};

export default MediaDownloadProvider;
